/*
 * Copyright (c) 2020. Sonrai Analytics Limited
 * Developed by Warren Dowey
 */

import { DataChangesModel } from './data-changes.model';

export class ProjectDataModel {
  private _projectDataId?: string;
  private _projectId?: string;
  private _dataType?: string;
  private _url?: string;
  private _workflowId?: string;
  private _step: number;
  private _userId: string;
  private _sessionId: string;
  private _createdAt: string;
  private _isActive: boolean;
  private _lastActivated: Date;
  private _uploaded: boolean;
  private _changes: DataChangesModel[];
  private _rawDataFile1: string;
  private _rawDataFile2: string;
  private _dataSetId: string;
  private _dataName: string;

  constructor(
    projectDataId?: string,
    projectId?: string,
    dataType?: string,
    url?: string,
    workflowId?: string,
    step?: number,
    userId?: string,
    sessionId?: string,
    createdAt?: string,
    isActive?: boolean,
    lastActivated?: Date,
    uploaded?: boolean,
    changes?: DataChangesModel[],
    rawDataFile1?: string,
    rawDataFile2?: string,
    dataSetId?: string,
    dataName?: string
  ) {
    this.projectDataId = projectDataId;
    this.projectId = projectId;
    this.dataType = dataType;
    this.url = url;
    this.workflowId = workflowId;
    this.step = step;
    this.userId = userId;
    this.sessionId = sessionId;
    this.createdAt = createdAt;
    this.isActive = isActive;
    this.lastActivated = lastActivated;
    this.uploaded = uploaded;
    this.changes = changes;
    this.rawDataFile1 = rawDataFile1;
    this.rawDataFile2 = rawDataFile2;
    this.dataSetId = dataSetId;
    this.dataName = dataName;
  }

  get projectDataId(): string {
    return this._projectDataId;
  }

  set projectDataId(value: string) {
    this._projectDataId = value;
  }

  get projectId(): string {
    return this._projectId;
  }

  set projectId(value: string) {
    this._projectId = value;
  }

  get dataType(): string {
    return this._dataType;
  }

  set dataType(value: string) {
    this._dataType = value;
  }

  get url(): string {
    return this._url;
  }

  set url(value: string) {
    this._url = value;
  }

  get workflowId(): string {
    return this._workflowId;
  }

  set workflowId(value: string) {
    this._workflowId = value;
  }

  get step(): number {
    return this._step;
  }

  set step(value: number) {
    this._step = value;
  }

  get userId(): string {
    return this._userId;
  }

  set userId(value: string) {
    this._userId = value;
  }

  get sessionId(): string {
    return this._sessionId;
  }

  set sessionId(value: string) {
    this._sessionId = value;
  }

  get createdAt(): string {
    return this._createdAt;
  }

  set createdAt(value: string) {
    this._createdAt = value;
  }

  public get isActive(): boolean {
    return this._isActive;
  }

  public set isActive(isActive: boolean) {
    this._isActive = isActive;
  }

  public get lastActivated(): Date {
    return this._lastActivated;
  }

  public set lastActivated(lastActivated: Date) {
    this._lastActivated = lastActivated;
  }

  public get uploaded(): boolean {
    return this._uploaded;
  }

  public set uploaded(uploaded: boolean) {
    this._uploaded = uploaded;
  }

  public get changes(): DataChangesModel[] {
    return this._changes;
  }

  public set changes(changes: DataChangesModel[]) {
    this._changes = changes;
  }

  public get rawDataFile1(): string {
    return this._rawDataFile1;
  }

  public set rawDataFile1(rawDataFile1: string) {
    this._rawDataFile1 = rawDataFile1;
  }

  public get rawDataFile2(): string {
    return this._rawDataFile2;
  }

  public set rawDataFile2(rawDataFile2: string) {
    this._rawDataFile2 = rawDataFile2;
  }

  public get dataSetId(): string {
    return this._dataSetId;
  }

  public set dataSetId(dataSetId: string) {
    this._dataSetId = dataSetId;
  }

  public get dataName(): string {
    return this._dataName;
  }

  public set dataName(dataName: string) {
    this._dataName = dataName;
  }

  toJson(): Object {
    const changes = this.changes.map((change) => {
      return change.toJson();
    });
    const object = {
      project_data_id: this.projectDataId,
      project_id: this.projectId,
      url: this.url,
      data_type: this.dataType,
      workflow_id: this.workflowId,
      step: this.step,
      user_id: this.userId,
      session_id: this.sessionId,
      created_at: this.createdAt,
      is_active: this.isActive,
      last_activated: this.lastActivated,
      uploaded: this.uploaded,
      changes: changes,
      raw_data_file1: this.rawDataFile1,
      raw_data_file2: this.rawDataFile2,
      data_set_id: this.dataSetId,
      data_name: this.dataName,
    };
    Object.keys(object).forEach(
      (k) =>
        !object[k] && typeof object[k] != 'boolean' && object[k] !== undefined && delete object[k]
    );
    return object;
  }

  fromJson(input: any): ProjectDataModel {
    const changesHolder: DataChangesModel[] = [];
    let dataChangesModel: DataChangesModel;
    if (input['changes']) {
      for (const change of input['changes']) {
        dataChangesModel = new DataChangesModel();
        dataChangesModel = dataChangesModel.fromJson(change);
        changesHolder.push(dataChangesModel);
      }
    }
    return new ProjectDataModel(
      input['project_data_id'],
      input['project_id'],
      input['data_type'],
      input['url'],
      input['workflow_id'],
      input['step'],
      input['user_id'],
      input['session_id'],
      input['created_at'],
      input['is_active'],
      input['last_activated'],
      input['uploaded'],
      changesHolder,
      input['raw_data_file1'],
      input['raw_data_file2'],
      input['data_set_id'],
      input['data_name']
    );
  }
}
